import React, { useState } from 'react';
import { Goal } from '../../Projects/Goals/index';
import GoalCard from '../../Projects/Goals/GoalCard';
import GoalForm from '../../Projects/Goals/GoalForm';
import { GoalFormData } from '../../Projects/Goals/index';
import { Milestone } from '../../Projects/Milestones/index';
import QuickValueUpdate from '../../Projects/Goals/QuickValueUpdate';

interface InlineEditGoalProps {
  goal: Goal;
  onSave: (goal: Partial<Goal> & { updateNotes?: string }) => Promise<void> | void;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
  milestones?: Milestone[];
}

const InlineEditGoal: React.FC<InlineEditGoalProps> = ({
  goal,
  onSave,
  onCancel,
  disabled = false,
  className = '',
  milestones = [],
}) => {
  // Extract milestoneId as string (handle both string and populated object)
  // Project milestones use 'id' field, not '_id', so we need to match against that
  const getMilestoneId = (): string => {
    if (!goal.milestoneId) return '';
    if (typeof goal.milestoneId === 'string') {
      // If it's a string, try to find matching milestone in the milestones array
      // and return the format that matches (id or _id)
      const matchingMilestone = milestones.find(
        (m) => (m.id === goal.milestoneId) || (m._id === goal.milestoneId)
      );
      if (matchingMilestone) {
        return matchingMilestone.id || matchingMilestone._id || goal.milestoneId;
      }
      return goal.milestoneId;
    }
    // If it's an object (populated), extract the ID
    const populatedId = goal.milestoneId._id || (goal.milestoneId as any).id || '';
    // Try to find matching milestone and return the format that matches
    if (populatedId) {
      const matchingMilestone = milestones.find(
        (m) => (m.id === populatedId) || (m._id === populatedId)
      );
      if (matchingMilestone) {
        return matchingMilestone.id || matchingMilestone._id || populatedId;
      }
    }
    return populatedId;
  };

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<GoalFormData>({
    _id: goal._id || goal.id,
    id: goal.id,
    title: goal.title,
    description: goal.description || '',
    targetValue: goal.targetValue,
    currentValue: goal.currentValue,
    unit: goal.unit,
    status: goal.status,
    outcome: goal.outcome,
    outcomeNotes: goal.outcomeNotes || '',
    conclusion: goal.conclusion || '',
    results: goal.results || '',
    delayReason: goal.delayReason || '',
    postponeReason: goal.postponeReason || '',
    cancelReason: goal.cancelReason || '',
    milestoneId: getMilestoneId(),
  });
  const [updateNotes, setUpdateNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEdit = () => {
    if (disabled) return;
    setFormData({
      _id: goal._id || goal.id,
      id: goal.id,
      title: goal.title,
      description: goal.description || '',
      targetValue: goal.targetValue,
      currentValue: goal.currentValue,
      unit: goal.unit,
      status: goal.status,
      outcome: goal.outcome,
      outcomeNotes: goal.outcomeNotes || '',
      conclusion: goal.conclusion || '',
      results: goal.results || '',
      delayReason: goal.delayReason || '',
      postponeReason: goal.postponeReason || '',
      cancelReason: goal.cancelReason || '',
      milestoneId: getMilestoneId(),
    });
    setUpdateNotes('');
    setIsEditing(true);
  };

  const handleFormDataChange = (data: Partial<GoalFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.targetValue || !formData.unit) return;

    setIsSaving(true);
    try {
      const updateData: any = {
        ...formData,
      };
      // Explicitly handle milestoneId: convert null/empty string to null, keep valid IDs as strings
      if (updateData.milestoneId === '' || updateData.milestoneId === null) {
        updateData.milestoneId = null;
      } else if (updateData.milestoneId) {
        // Ensure milestoneId is a string (not undefined)
        updateData.milestoneId = String(updateData.milestoneId);
      }
      if (updateNotes) {
        updateData.updateNotes = updateNotes;
      }
      await onSave(updateData);
      setIsEditing(false);
      setUpdateNotes('');
    } catch (error) {
      console.error('Error saving goal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setUpdateNotes('');
    onCancel?.();
  };

  const handleQuickValueUpdate = async (newValue: number) => {
    if (disabled) return;
    setIsSaving(true);
    try {
      await onSave({
        currentValue: newValue,
        updateNotes: `Value updated from ${goal.currentValue} to ${newValue}`,
      } as any);
    } catch (error) {
      console.error('Error updating goal value:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className={`inline-edit-wrapper ${className}`}>
        <div className="animate-slide-down">
          <GoalForm
            formData={formData}
            onFormDataChange={handleFormDataChange}
            milestones={milestones}
            updateNotes={updateNotes}
            onUpdateNotesChange={setUpdateNotes}
            showUpdateNotes={true}
            onSubmit={handleSave}
            onCancel={handleCancel}
            isLoading={isSaving}
            submitLabel="Save"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`inline-edit-display group ${className}`}>
      <div className="relative">
        <GoalCard
          goal={goal}
          showActions={false}
        />
        {!disabled && (
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-2 bg-white rounded-lg p-2 shadow-md">
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500">{goal.unit}:</span>
              <QuickValueUpdate
                currentValue={goal.currentValue}
                onValueChange={handleQuickValueUpdate}
                className="w-20"
              />
            </div>
            <button
              type="button"
              onClick={handleStartEdit}
              className="ml-1"
              aria-label="Edit goal"
            >
              <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InlineEditGoal;

